import { useState } from 'react';
import { KeyRound, Save, Trash2, Sparkles } from 'lucide-react';
import InfoTip from './InfoTip';
import { formatRelative } from '../lib/format';

// One card per AI provider (Anthropic / OpenAI). Used twice from AccountTab.
// Encapsulates: status pill, password input, save button, optional remove
// button, optional model dropdown, and the "created Xd ago · last used Yd ago"
// footer when a key is saved.
export default function ProviderKeyCard({
  label,
  placeholder,
  hint,
  description,
  hasKey,
  preview,
  isActive,
  isOverridden,
  createdAt,
  lastUsedAt,
  model,
  modelOptions = [],
  onSave,
  onDelete,
  onSelectModel,
  saving = false,
  deleting = false,
  embedded = false,
}) {
  const [value, setValue] = useState('');
  const submit = () => {
    if (!value) return;
    onSave(value);
    setValue('');
  };

  // When `embedded`, render contents without the outer .card wrapper and use a
  // smaller subsection header — caller is responsible for the surrounding card.
  const Wrapper = embedded ? 'div' : 'div';
  const wrapperClass = embedded ? '' : 'card';

  return (
    <Wrapper className={wrapperClass}>
      <div className={embedded ? 'flex items-center justify-between mb-2' : 'section-header'}>
        <div className={embedded ? 'flex items-center gap-2 text-sm font-medium text-ink-700' : 'section-title'}>
          {embedded ? (
            <Sparkles className="w-4 h-4 text-ink-400" />
          ) : (
            <span className="section-icon">
              <Sparkles className="w-4 h-4" />
            </span>
          )}
          {label}
          {hint && <InfoTip title={hint.title} body={hint.body} />}
        </div>
        {hasKey ? (
          isActive ? (
            <span className="pill-success">
              <KeyRound className="w-3 h-3" /> Active
            </span>
          ) : isOverridden ? (
            <span className="pill bg-amber-50 text-amber-700">
              <KeyRound className="w-3 h-3" /> Saved (overridden)
            </span>
          ) : (
            <span className="pill bg-gray-100 text-ink-500">
              <KeyRound className="w-3 h-3" /> Saved (not preferred)
            </span>
          )
        ) : (
          <span className="pill bg-gray-100 text-ink-500">
            <KeyRound className="w-3 h-3" /> Not configured
          </span>
        )}
      </div>

      {description && <p className="text-sm text-gray-600 mb-3">{description}</p>}

      {modelOptions.length > 0 && (
        <div className="mb-3">
          <label className="label">Model</label>
          <select
            className="input"
            value={model || ''}
            onChange={(e) => onSelectModel?.(e.target.value)}
            disabled={saving}
          >
            {modelOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>
      )}

      {hasKey && (
        <div className="mb-2 flex items-center gap-2 flex-wrap">
          {preview && (
            <div className="inline-flex items-center gap-2 text-xs bg-gray-50 border border-gray-100 rounded-md px-2.5 py-1.5">
              <KeyRound className="w-3 h-3 text-ink-400" />
              <span className="text-ink-500">Saved:</span>
              <code className="font-mono text-ink-700">{preview}</code>
            </div>
          )}
          {onDelete && (
            <button
              type="button"
              className="text-xs inline-flex items-center gap-1 text-rose-600 hover:text-rose-700 hover:bg-rose-50 px-2 py-1 rounded-md transition-colors disabled:opacity-50"
              disabled={deleting}
              onClick={() => {
                if (
                  window.confirm(
                    `Remove your saved ${label}? AI features will fall back to the next provider.`,
                  )
                ) {
                  onDelete();
                }
              }}
            >
              <Trash2 className="w-3 h-3" /> {deleting ? 'Removing…' : 'Remove key'}
            </button>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
          <input
            className="input pl-10"
            type="password"
            placeholder={hasKey ? 'Enter a new key to replace…' : placeholder}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
        </div>
        <button className="btn-primary" onClick={submit} disabled={saving || !value}>
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
        </button>
      </div>

      {hasKey && (createdAt || lastUsedAt) && (
        <div className="mt-3 pt-3 border-t border-gray-50 text-xs text-ink-400 flex items-center gap-3 flex-wrap">
          {createdAt && <span>Created {formatRelative(createdAt)}</span>}
          {lastUsedAt && <span>· Last used {formatRelative(lastUsedAt)}</span>}
          {!lastUsedAt && createdAt && <span>· Not used yet</span>}
        </div>
      )}
    </Wrapper>
  );
}
